/**
 * useTimelineLabels — maps the `timeline` i18n namespace to the
 * presentational `DailyTimelineLabels` contract required by `<DailyTimeline />`.
 *
 * UX Invariant §9: no hardcoded user-facing strings. The timeline component is
 * domain-agnostic and accepts `labels` as a prop; this hook is the canonical
 * bridge between the i18next store and that prop. Keeps pluralisation /
 * interpolation concerns out of the dumb UI layer.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { AppointmentStatus } from '../calendar/types';
import type { DailyTimelineLabels } from './types';

const STATUS_KEYS: AppointmentStatus[] = [
  'confirmed',
  'pending',
  'cancelled',
  'no_show',
  'in_progress',
  'completed',
];

const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
const MONTH_KEYS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
] as const;

export function useTimelineLabels(): DailyTimelineLabels {
  const { t } = useTranslation('timeline');

  return useMemo(() => {
    const statusLabels = STATUS_KEYS.reduce<Record<AppointmentStatus, string>>(
      (acc, key) => {
        acc[key] = t(`statusLabels.${key}`);
        return acc;
      },
      {} as Record<AppointmentStatus, string>,
    );

    const weekdayShort = WEEKDAY_KEYS.map((k) => t(`weekdayShort.${k}`)) as [
      string,
      string,
      string,
      string,
      string,
      string,
      string,
    ];

    const monthsLong = MONTH_KEYS.map((k) => t(`monthsLong.${k}`)) as [
      string,
      string,
      string,
      string,
      string,
      string,
      string,
      string,
      string,
      string,
      string,
      string,
    ];

    return {
      title: t('title'),
      prevDay: t('prevDay'),
      nextDay: t('nextDay'),
      today: t('today'),
      summary: ({ done, active, remaining }) =>
        t('summary', { done, active, remaining }),
      segment: {
        all: t('segment.all'),
        active: t('segment.active'),
        done: t('segment.done'),
      },
      nowMarker: t('nowMarker'),
      nextCta: ({ time, minutes }) => {
        if (time === undefined || minutes === undefined) return t('nextCtaEmpty');
        if (minutes <= 0) return t('nextCtaNow', { time });
        return t('nextCta', { time, minutes });
      },
      nextCtaDone: t('nextCtaDone'),
      nextCtaLate: ({ minutes }) => t('nextCtaLate', { minutes }),
      offlineBanner: t('offlineBanner'),
      statusLabels,
      countdown: ({ minutes }) => t('countdown', { minutes }),
      elapsed: ({ minutes }) => t('elapsed', { minutes }),
      priceLabel: t('priceLabel'),
      notesLabel: t('notesLabel'),
      customerLabel: t('customerLabel'),
      vipBadge: t('vipBadge'),
      callAction: t('callAction'),
      editAction: t('editAction'),
      completeAction: t('completeAction'),
      cancelAction: t('cancelAction'),
      markNoShowAction: t('markNoShowAction'),
      markInProgressAction: t('markInProgressAction'),
      largeModeOn: t('largeModeOn'),
      largeModeOff: t('largeModeOff'),
      empty: t('empty'),
      errorTitle: t('errorTitle'),
      errorRetry: t('errorRetry'),
      swipeHintComplete: t('swipeHintComplete'),
      swipeHintCancel: t('swipeHintCancel'),
      cancelReasonTitle: t('cancelReasonTitle'),
      cancelReasonPlaceholder: t('cancelReasonPlaceholder'),
      cancelReasonConfirm: t('cancelReasonConfirm'),
      cancelReasonDismiss: t('cancelReasonDismiss'),
      noShowConfirmTitle: t('noShowConfirmTitle'),
      noShowConfirmBody: t('noShowConfirmBody'),
      noShowConfirmYes: t('noShowConfirmYes'),
      noShowConfirmNo: t('noShowConfirmNo'),
      detailsTitle: t('detailsTitle'),
      closeSheet: t('closeSheet'),
      weekdayShort,
      monthsLong,
      desktopHint: t('desktopHint'),
    };
  }, [t]);
}
